import type { Order, OrderItem } from '../../types';
import {
    OrderStatus,
    PaymentFamily,
    DeliveryMethod,
    DeliveryStatus,
    ProductStatus,
    NotificationType,
    paymentFamilyToCode,
} from '../../types';
import { formatCurrency, translateError, getOrderStatusLabel } from '../../utils/helpers';
import { supabase } from '../supabaseClient';
import { deliveryService } from '../deliveryService';
import { notifyUser, notifyAdmins } from '../notificationService';
import { uploadFile } from '../storage/publicAssets';
import { fetchAllOrdersAdmin, fetchOrdersForUser } from './orderQueries';
import type { NewOrderData } from '../api/contracts';
import { devError } from '@/utils/devLog';

export async function checkStockForItems(items: { productId: number; quantity: number; deliveryMethod?: string }[]): Promise<void> {
    const CODE_INSTANTANE = DeliveryMethod.CODE_INSTANTANE;
    const DEFERRED_DIGITAL: DeliveryMethod[] = [
        DeliveryMethod.TRAITEMENT_LIEN,
        DeliveryMethod.MAIL_COMPTE_CODE,
        DeliveryMethod.MAIL_ACCES,
        DeliveryMethod.MAIL_CODE,
        DeliveryMethod.MAIL_LICENCE,
    ];
    const PHYSICAL = DeliveryMethod.LIVRAISON_DOMICILE;

    const productIds = items.filter(i => i.quantity > 0).map(i => i.productId);
    const { data: productsRows } = productIds.length > 0
        ? await supabase.from('products').select('id, delivery_method, statut, nom, stock_quantity').in('id', productIds)
        : { data: [] };
    const productMap: Record<number, { delivery_method: string; statut?: string; nom?: string; stock_quantity?: number }> = {};
    (productsRows || []).forEach((p: any) => { productMap[p.id] = p; });

    for (const { productId, quantity, deliveryMethod } of items) {
        if (quantity <= 0) continue;
        const product = productMap[productId];
        const method = (deliveryMethod || product?.delivery_method || '') as DeliveryMethod;
        if (method === CODE_INSTANTANE) {
            let available = 0;
            const { data: availableCount, error } = await supabase.rpc('get_digital_codes_available_count', { p_product_id: productId });
            if (!error && availableCount != null) available = Number(availableCount);
            if (available === 0) {
                const now = new Date().toISOString();
                const { count: fallbackCount } = await supabase
                    .from('digital_codes')
                    .select('*', { count: 'exact', head: true })
                    .eq('product_id', productId)
                    .or('status.eq.AVAILABLE,status.eq.available')
                    .or('expiration_date.is.null,expiration_date.gt.' + now);
                if (fallbackCount != null && fallbackCount > 0) available = fallbackCount;
            }
            if (available < quantity) {
                if (available === 0) throw new Error('Produit épuisé.');
                throw new Error('Stock insuffisant : ' + available + ' disponible(s), ' + quantity + ' demandé(s).');
            }
        } else if (DEFERRED_DIGITAL.includes(method)) {
            if (product?.statut !== ProductStatus.ON_SALE) {
                throw new Error('Produit ' + (product?.nom || productId) + ' est actuellement épuisé ou indisponible.');
            }
        } else if (method === PHYSICAL) {
            const available = Number(product?.stock_quantity ?? 0);
            if (available < quantity) {
                if (available === 0) throw new Error('Produit ' + (product?.nom || productId) + ' en rupture.');
                throw new Error('Stock insuffisant pour ' + (product?.nom || productId) + ' : ' + available + ' disponible(s).');
            }
        }
    }
}

export const orderMethods = {
getAllOrders: (): Promise<Order[]> => fetchAllOrdersAdmin(),

getOrders: (userId: string): Promise<Order[]> => fetchOrdersForUser(userId),

submitOrder: async (data: NewOrderData): Promise<void> => {
    let preuveUrl = '';
    // La policy RLS du bucket 'proofs' exige un chemin {user_uuid}/{nom} (auth.uid() = storage.foldername(name)[1]).
    if (data.preuve) preuveUrl = await uploadFile('proofs', data.preuve, `${data.user_id}/${Date.now()}_${data.preuve.name.replace(/\s+/g, '_')}`);
    else if (data.transaction_id) preuveUrl = data.transaction_id;

    // 1. Vérification stock selon le type de produit (physique / code instantané / code différé)
    const CODE_INSTANTANE = DeliveryMethod.CODE_INSTANTANE;
    const DEFERRED_DIGITAL: DeliveryMethod[] = [
        DeliveryMethod.TRAITEMENT_LIEN,
        DeliveryMethod.MAIL_COMPTE_CODE,
        DeliveryMethod.MAIL_ACCES,
        DeliveryMethod.MAIL_CODE,
        DeliveryMethod.MAIL_LICENCE,
    ];
    const PHYSICAL = DeliveryMethod.LIVRAISON_DOMICILE;

    const itemProductIds = Array.from(new Set((data.items || []).map((i) => i.produit_id)));
    const { data: productsRows } = await supabase.from('products').select('id, delivery_method, statut, stock_quantity, nom').in('id', itemProductIds);
    const productById: Record<number, { delivery_method: string; statut: string; stock_quantity?: number; nom?: string }> = {};
    (productsRows || []).forEach((p: any) => { productById[p.id] = p; });

    for (const item of data.items || []) {
        const productId = item.produit_id;
        const requestedQty = item.quantite || 1;
        const product = productById[productId];
        const method = (item.delivery_method ?? product?.delivery_method) as DeliveryMethod | undefined;
        if (!method || !product) continue;

        if (method === CODE_INSTANTANE) {
            let available = 0;
            const { data: availableCount, error: stockError } = await supabase
                .rpc('get_digital_codes_available_count', { p_product_id: productId });
            if (!stockError && availableCount != null) available = Number(availableCount);
            if (available === 0) {
                const now = new Date().toISOString();
                const { count: fallbackCount } = await supabase
                    .from('digital_codes')
                    .select('*', { count: 'exact', head: true })
                    .eq('product_id', productId)
                    .or('status.eq.AVAILABLE,status.eq.available')
                    .or('expiration_date.is.null,expiration_date.gt.' + now);
                if (fallbackCount != null && fallbackCount > 0) available = fallbackCount;
            }
            if (available < requestedQty) {
                const err: any = new Error('INSUFFICIENT_STOCK');
                err.code = 'INSUFFICIENT_STOCK';
                err.requested = requestedQty;
                err.available = available;
                err.productId = productId;
                throw err;
            }
        } else if (DEFERRED_DIGITAL.includes(method)) {
            if (product.statut !== ProductStatus.ON_SALE) {
                const err: any = new Error(product.nom ? `Le produit « ${product.nom} » est actuellement épuisé.` : 'Ce produit est actuellement épuisé.');
                err.code = 'PRODUCT_OUT_OF_STOCK';
                err.productId = productId;
                err.productName = product.nom;
                throw err;
            }
        } else if (method === PHYSICAL) {
            const available = Number(product.stock_quantity ?? 0);
            if (available < requestedQty) {
                const err: any = new Error('INSUFFICIENT_STOCK');
                err.code = 'INSUFFICIENT_STOCK';
                err.requested = requestedQty;
                err.available = available;
                err.productId = productId;
                throw err;
            }
        }
    }

    // 2. Prélèvement du solde si nécessaire
    if (data.mode_paiement === PaymentFamily.SOLDE) {
        const { data: user } = await supabase.from('profiles').select('solde').eq('id', data.user_id).single();
        if (!user || user.solde < data.montant_total) throw new Error("Solde insuffisant.");
        
        const { error: balanceError } = await supabase.from('profiles').update({ solde: user.solde - data.montant_total }).eq('id', data.user_id);
        if (balanceError) throw new Error("Erreur lors du prélèvement du solde.");
    }

    // 3. Création de la commande
    const paymentMethodCode = data.payment_method_detail || (data.mode_paiement ? paymentFamilyToCode(data.mode_paiement) : null);

    // Générer les numéros de tracking pour les articles livraison à domicile
    const itemsWithTracking = data.items.map((item, idx) => {
      if (item.delivery_method === DeliveryMethod.LIVRAISON_DOMICILE) {
        const trackingNumber = `ES-TRK-${Date.now()}-${idx}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
        return {
          ...item,
          delivery_metadata: {
            ...(item.delivery_metadata || {}),
            tracking_number: trackingNumber,
          },
        };
      }
      return item;
    });

    const orderInsert: Record<string, any> = {
      user_id: data.user_id,
      items: itemsWithTracking,
      montant_total: data.montant_total,
      payment_method_detail: paymentMethodCode,
      payment_setting_id: data.payment_setting_id || null,
      preuve: preuveUrl,
      transaction_id: data.transaction_id,
      statut: OrderStatus.PENDING,
      date: new Date().toISOString(),
    };
    if (data.delivery_fee != null) orderInsert.delivery_fee = data.delivery_fee;
    if (data.stamp_fee != null) orderInsert.stamp_fee = data.stamp_fee;

    const { data: order, error: insertError } = await supabase.from('orders').insert(orderInsert).select().single();

    if (insertError) throw new Error(translateError(insertError));

    // 4. Notification Administrateurs — metadata pour i18n
    await notifyAdmins(
        `Nouvelle commande #${order.id} reçue (${formatCurrency(data.montant_total)})`,
        NotificationType.COMMANDE_STATUT,
        `/admin/orders`,
        undefined,
        { order_id: order.id, amount: data.montant_total }
    );

    // 5. Livraison AUTOMATIQUE
    // NOTE: Les produits CODE_INSTANTANE seront automatiquement ignorés par deliveryService
    // et ne seront livrés que lorsque l'admin passe la commande à "Terminée"
    if (data.mode_paiement === PaymentFamily.SOLDE) {
        try {
            await deliveryService.processOrderDelivery(order);
        } catch (deliveryErr: any) {
            devError("Auto-delivery failure:", deliveryErr);
        }
    }
},

updateOrderStatus: async (id: number, statut: OrderStatus): Promise<void> => {
    // 1. Récupérer la commande pour voir si elle a besoin d'une livraison numérique
    const { data: orderData, error: fetchError } = await supabase.from('orders').select('*').eq('id', id).single();
    if (fetchError || !orderData) throw new Error("Commande introuvable.");
    const orderItems = Array.isArray(orderData.items) ? orderData.items : [];

    // Verrou métier: une commande déjà terminée ne doit plus pouvoir revenir en arrière.
    if (orderData.statut === OrderStatus.COMPLETED && statut !== OrderStatus.COMPLETED) {
        throw new Error("Cette commande est déjà terminée et son statut est verrouillé.");
    }

    // Garde-fou anti double livraison (cas historique: commande repassée en attente puis re-terminée).
    // Si un code instantané a déjà été assigné sur la commande, une nouvelle transition vers
    // "Terminée" est interdite pour éviter une seconde attribution de codes.
    const hasInstantCodeAlreadyDelivered = orderItems.some((i: any) => {
        const method = (i?.delivery_method || '').toLowerCase();
        if (method !== DeliveryMethod.CODE_INSTANTANE) return false;
        const hasDeliveredStatus = i?.delivery_status === DeliveryStatus.DELIVERED;
        const codes = i?.delivery_metadata?.codes;
        const hasAssignedCodes = Array.isArray(codes) && codes.length > 0;
        return hasDeliveredStatus || hasAssignedCodes;
    });
    if (statut === OrderStatus.COMPLETED && orderData.statut !== OrderStatus.COMPLETED && hasInstantCodeAlreadyDelivered) {
        throw new Error("Impossible de revalider cette commande: des codes instantanés ont déjà été livrés.");
    }

    // 2. Si l'admin passe à "Terminée", on force la livraison des codes s'ils ne le sont pas déjà (pas pour Annulée / En litige)
    if (statut === OrderStatus.COMPLETED) {
        const hasPendingDigitalItems = orderItems.some((i: any) =>
            [DeliveryMethod.CODE_INSTANTANE, DeliveryMethod.MAIL_CODE, DeliveryMethod.MAIL_LICENCE, DeliveryMethod.MAIL_COMPTE_CODE, DeliveryMethod.MAIL_ACCES].includes(i.delivery_method) &&
            (!i.delivery_status || i.delivery_status !== DeliveryStatus.DELIVERED)
        );

        if (hasPendingDigitalItems) {
            try {
                await deliveryService.processOrderDelivery(orderData, OrderStatus.COMPLETED);
            } catch (deliveryErr: any) {
                devError("Erreur lors du traitement de la livraison:", deliveryErr);
            }
        }
    }

    // 3. Mise à jour du statut
    const { error } = await supabase.from('orders').update({ statut }).eq('id', id);
    if (error) throw new Error(translateError(error));

    // 4. Notifier le client (type spécifique pour annulée / litige) — metadata pour i18n côté client
    try {
        const notifType = statut === OrderStatus.CANCELLED
            ? NotificationType.COMMANDE_ANNULEE
            : statut === OrderStatus.DISPUTE
                ? NotificationType.COMMANDE_LITIGE
                : NotificationType.COMMANDE_STATUT;
        const message = `Le statut de votre commande #${id} est maintenant : ${getOrderStatusLabel(statut)}`;
        await notifyUser(orderData.user_id, message, notifType, '/dashboard/orders', undefined, { order_id: id, order_status: statut });
    } catch (e) {
        devError("Failed to notify user for order update", e);
    }
},

/** Supprime une commande (RPC admin si disponible, sinon DELETE direct). En cas de message « interdite », exécuter database_files/migration_admin_delete_orders.sql dans Supabase. */
deleteOrder: async (id: number): Promise<void> => {
    const orderId = Number(id);
    const { error: rpcError } = await supabase.rpc('admin_delete_order', { p_order_id: orderId });
    if (!rpcError) return;
    if (rpcError.code === 'PGRST202' || rpcError.message?.includes('function') || rpcError.message?.includes('does not exist')) {
        await supabase.from('delivery_logs').delete().eq('order_id', orderId);
        await supabase.from('order_issues').delete().or(`order_id.eq.${orderId},resolution_order_id.eq.${orderId}`);
        await supabase.from('admin_order_audit_log').delete().or(`order_id_original.eq.${orderId},order_id_new.eq.${orderId}`);
        await supabase.from('orders').update({ parent_order_id: null, type: 'NORMAL' }).eq('parent_order_id', orderId);
        const { error } = await supabase.from('orders').delete().eq('id', orderId);
        if (error) throw new Error(translateError(error));
        return;
    }
    throw new Error(translateError(rpcError));
},

/** Supprime plusieurs commandes en lot (RPC admin si disponible, sinon DELETE direct). */
deleteOrders: async (ids: number[]): Promise<void> => {
    if (!ids.length) return;
    const orderIds = ids.map((id) => Number(id));
    const { error: rpcError } = await supabase.rpc('admin_delete_orders', { p_order_ids: orderIds });
    if (!rpcError) return;
    if (rpcError.code === 'PGRST202' || rpcError.message?.includes('function') || rpcError.message?.includes('does not exist')) {
        await supabase.from('delivery_logs').delete().in('order_id', orderIds);
        await supabase.from('order_issues').delete().in('order_id', orderIds);
        await supabase.from('order_issues').delete().in('resolution_order_id', orderIds);
        await supabase.from('admin_order_audit_log').delete().in('order_id_original', orderIds);
        await supabase.from('admin_order_audit_log').delete().in('order_id_new', orderIds);
        await supabase.from('orders').update({ parent_order_id: null, type: 'NORMAL' }).in('parent_order_id', orderIds);
        const { error } = await supabase.from('orders').delete().in('id', orderIds);
        if (error) throw new Error(translateError(error));
        return;
    }
    throw new Error(translateError(rpcError));
},

/**
 * Crée une commande de remplacement ou de compensation (sans modifier la commande d’origine).
 * Nécessite la migration migration_orders_replacement.sql (orders.type, parent_order_id, order_issues, admin_order_audit_log).
 */
createReplacementOrder: async (
    originalOrderId: number,
    payload: {
        issueType: string;
        action: string;
        adminNote?: string;
        internalRiskLevel?: 'low' | 'medium' | 'high';
        evidenceRequested?: boolean;
        replacementQuantities?: { itemIndex: number; quantityToReplace: number }[];
        compensationItems?: OrderItem[];
    }
): Promise<{ newOrderId?: number; message: string }> => {
    const { data: { session } } = await supabase.auth.getSession();
    const adminId = session?.user?.id;
    if (!adminId) throw new Error("Vous devez être connecté pour effectuer cette action.");

    const { data: originalOrder, error: fetchErr } = await supabase
        .from('orders')
        .select('*')
        .eq('id', originalOrderId)
        .single();
    if (fetchErr || !originalOrder) throw new Error("Commande d'origine introuvable.");
    if (originalOrder.statut !== OrderStatus.COMPLETED) {
        throw new Error("Seules les commandes déjà livrées peuvent faire l'objet d'une commande de remplacement.");
    }

    const action = payload.action as 'REPLACEMENT' | 'COMPENSATION' | 'REFUND';
    const issueType = payload.issueType as string;

    if (action === 'REFUND') {
        // Marquer pour remboursement : enregistrer le problème, pas de nouvelle commande
        const { error: issueErr } = await supabase.from('order_issues').insert({
            order_id: originalOrderId,
            issue_type: issueType,
            reported_by: 'ADMIN',
            status: 'REFUNDED',
            description: payload.adminNote || null,
        });
        if (issueErr) throw new Error(translateError(issueErr));

        await supabase.from('orders').update({ workflow_status: 'RESOLVED' }).eq('id', originalOrderId);
        await supabase.from('admin_order_audit_log').insert({
            admin_id: adminId,
            order_id_original: originalOrderId,
            order_id_new: null,
            action: 'MARK_REFUND',
            reason: payload.adminNote || issueType,
            metadata: {
                issueType,
                internalRiskLevel: payload.internalRiskLevel ?? null,
                evidenceRequested: payload.evidenceRequested ?? false,
            },
        });
        return { message: "Problème enregistré pour remboursement. Aucune nouvelle commande créée." };
    }

    // REPLACEMENT ou COMPENSATION : construire les items puis vérifier le stock
    let newItems: OrderItem[];
    if (action === 'REPLACEMENT') {
        const items = originalOrder.items as OrderItem[];
        const quantities = payload.replacementQuantities ?? [];
        if (!quantities.length) throw new Error("Indiquez les quantités à remplacer pour chaque ligne.");
        newItems = quantities
            .filter(q => q.quantityToReplace > 0)
            .map(({ itemIndex, quantityToReplace }) => {
                const item = items[itemIndex];
                if (!item) throw new Error("Index d'item invalide.");
                if (quantityToReplace > (item.quantite ?? 0)) throw new Error(`Quantité à remplacer invalide pour "${item.nom_produit}".`);
                return { ...item, quantite: quantityToReplace };
            });
        if (!newItems.length) throw new Error("Au moins une quantité à remplacer doit être supérieure à 0.");
        await checkStockForItems(newItems.map(i => ({ productId: i.produit_id, quantity: i.quantite, deliveryMethod: i.delivery_method })));
    } else {
        const comp = payload.compensationItems ?? [];
        if (!comp.length) throw new Error("Choisissez au moins un produit pour la compensation.");
        newItems = comp.map(i => ({ ...i, quantite: i.quantite || 1 }));
        await checkStockForItems(newItems.map(i => ({ productId: i.produit_id, quantity: i.quantite, deliveryMethod: i.delivery_method })));
    }
    const orderType = action === 'REPLACEMENT' ? 'REPLACEMENT' : 'COMPENSATION';
    const newOrderPayload: Record<string, unknown> = {
        user_id: originalOrder.user_id,
        items: newItems,
        montant_total: 0,
        payment_method_detail: originalOrder.payment_method_detail || (originalOrder.mode_paiement ? paymentFamilyToCode(originalOrder.mode_paiement) : undefined),
        payment_setting_id: originalOrder.payment_setting_id || null,
        statut: OrderStatus.PENDING,
        date: new Date().toISOString(),
        type: orderType,
        parent_order_id: originalOrderId,
        created_by: 'ADMIN',
        workflow_status: 'PROCESSING',
        admin_note: payload.adminNote || null,
    };

    const { data: newOrder, error: insertErr } = await supabase
        .from('orders')
        .insert(newOrderPayload)
        .select()
        .single();
    if (insertErr) {
        const msg = translateError(insertErr);
        if (msg.includes('column') && (msg.includes('type') || msg.includes('parent_order_id') || msg.includes('workflow_status'))) {
            throw new Error("La base de données doit être migrée (migration_orders_replacement.sql) pour créer une commande de remplacement.");
        }
        throw new Error(msg);
    }

    try {
        await deliveryService.processOrderDelivery(newOrder as Order, OrderStatus.COMPLETED);
    } catch (deliveryErr: any) {
        devError("Erreur livraison commande de remplacement:", deliveryErr);
    }
    await supabase.from('orders').update({ statut: OrderStatus.COMPLETED, workflow_status: 'DELIVERED' }).eq('id', newOrder.id);

    const { error: issueErr } = await supabase.from('order_issues').insert({
        order_id: originalOrderId,
        issue_type: issueType,
        reported_by: 'ADMIN',
        status: 'REPLACED',
        resolution_order_id: newOrder.id,
        description: payload.adminNote || null,
    });
    if (issueErr) console.warn("order_issues insert (audit):", issueErr);

    await supabase.from('orders').update({ workflow_status: 'RESOLVED' }).eq('id', originalOrderId);

    await supabase.from('admin_order_audit_log').insert({
        admin_id: adminId,
        order_id_original: originalOrderId,
        order_id_new: newOrder.id,
        action: orderType === 'REPLACEMENT' ? 'CREATE_REPLACEMENT' : 'CREATE_COMPENSATION',
        reason: payload.adminNote || issueType,
        metadata: {
            issueType,
            internalRiskLevel: payload.internalRiskLevel ?? null,
            evidenceRequested: payload.evidenceRequested ?? false,
        },
    });

    try {
        await notifyUser(
            originalOrder.user_id,
            `Une commande de remplacement (#${newOrder.id}) a été créée pour votre commande #${originalOrderId}. Les nouveaux codes sont disponibles dans Mes Codes.`,
            NotificationType.COMMANDE_LIVREE,
            '/dashboard/orders',
            undefined,
            { replacement_order_id: newOrder.id, original_order_id: originalOrderId }
        );
    } catch (e) {
        console.warn("Notification client:", e);
    }

    return { newOrderId: newOrder.id, message: `Commande de remplacement #${newOrder.id} créée et livrée. Le client a été notifié.` };
},

updateOrderDocuments: async (id: number, docs: Partial<Order>): Promise<void> => {
    const { error } = await supabase.from('orders').update(docs).eq('id', id);
    if (error) throw new Error(translateError(error));
},
updatePhysicalDelivery: async (id: number, itemIndex: number, status: DeliveryStatus, tracking?: string) => { await deliveryService.updatePhysicalStatus(id, itemIndex, status, tracking); },

};
